import {
  SkeletalFrame,
  SkeletalPose,
  SkeletalPosition,
} from "../../lib/types/statsApi";
import { nearestFrameIndex } from "../../lib/misc/util";
import { RingBuffer } from "ring-buffer-ts";

// TODO: do we want to make positions a Map in modifyData?
export function extractPosition(
  frame: SkeletalFrame,
  id: number
): SkeletalPosition | undefined {
  let position = undefined;
  for (const pos of frame.positions) {
    if (pos.positionId === id) {
      position = pos;
      break;
    }
  }

  return position;
}

export type Frame = SkeletalFrame;

export type TaggedNeighbor<T> = { neighbor: T; time: number } | undefined;

export class Frames {
  buffer: RingBuffer<SkeletalFrame>;

  indices: {
    missed?: number;
    current?: number;
    max?: number;
  };

  constructor(buffer: RingBuffer<SkeletalFrame>) {
    this.buffer = buffer;
    this.indices = {};
  }

  get current(): Frame | undefined {
    return this.indices.current != undefined
      ? this.buffer.get(this.indices.current)
      : undefined;
  }

  get next(): Frame | undefined {
    return this.indices.current != undefined &&
      this.indices.max != undefined &&
      this.indices.current + 1 <= this.indices.max
      ? this.buffer.get(this.indices.current + 1)
      : undefined;
  }

  // TODO: see if we can use iterator / generator syntax to support for x of missed and
  //       not recreate array
  get missed(): Frame[] {
    const missed: Frame[] = [];

    if (this.indices.missed == undefined || this.indices.current == undefined) {
      return missed;
    }

    for (let i = this.indices.missed; i < this.indices.current; i++) {
      const frame = this.buffer.get(i);
      if (frame) missed.push(frame);
    }
    return missed;
  }

  getNeighbours<T>(
    extract: (frame: SkeletalFrame) => T | undefined
  ): { prev: TaggedNeighbor<T>; next: TaggedNeighbor<T> } | undefined {
    if (this.indices.current == undefined || this.indices.max == undefined) {
      return undefined;
    }

    let prev: TaggedNeighbor<T> = undefined;
    let next: TaggedNeighbor<T> = undefined;
    for (let i = this.indices.current; i >= 0; i--) {
      const frame = this.buffer.get(i);
      if (!frame) continue;

      const query = extract(frame);
      if (query != undefined) {
        prev = { neighbor: query, time: frame.time };
        break;
      }
    }

    if (prev) {
      for (let i = this.indices.current + 1; i <= this.indices.max; i++) {
        const frame = this.buffer.get(i);
        if (!frame) continue;

        const query = extract(frame);
        if (query != undefined) {
          next = { neighbor: query, time: frame.time };
          break;
        }
      }
    }

    return { prev, next };
  }
}

export class FrameManager {
  private lastTimestamp?: number;
  private maxMissedFrames = 300;

  private lastFrames?: Frames;

  constructor(maxMissedFrames?: number) {
    if (maxMissedFrames != undefined) {
      this.maxMissedFrames = maxMissedFrames;
    }
  }

  getFrames(
    trackingFrames: RingBuffer<SkeletalFrame>,
    timestamp?: number
  ): Frames {
    const frames = new Frames(trackingFrames);

    if (timestamp == this.lastTimestamp && this.lastFrames) {
      return this.lastFrames;
    }

    if (timestamp == undefined) {
      const last = trackingFrames.getLast();
      if (last) {
        timestamp = last.time;
      } else {
        console.error("empty tracking buffer - can't get latest");
        return frames;
      }
    }

    frames.indices.current = nearestFrameIndex(trackingFrames, timestamp);

    if (frames.indices.current != undefined) {
      // find index since last call
      const lastFrameIndex =
        this.lastTimestamp != undefined
          ? nearestFrameIndex(trackingFrames, this.lastTimestamp)
          : -1;
      if (lastFrameIndex != undefined) {
        const missedFrames = frames.indices.current - (lastFrameIndex + 1);
        if (missedFrames > 0 && missedFrames < this.maxMissedFrames) {
          frames.indices.missed = lastFrameIndex + 1;
        }
      }

      frames.indices.max = trackingFrames.getBufferLength() - 1;
    } else {
      console.error("invalid ceiling in binary search", timestamp);
    }

    this.lastTimestamp = timestamp;

    return frames;
  }
}
